import { Ctrl, Meter } from './protocol';
import type { HostToWorklet, WorkletToHost, ProgramConfig, RecordSource, SliceMode } from './protocol';
import type { AudioSystem } from './AudioSystem';

export type EngineEvent =
  | { type: 'ready' }
  | { type: 'sampleRecorded'; bank: number; frames: number }
  | { type: 'sampleOverview'; bank: number; peaks: Float32Array }
  | { type: 'log'; msg: string };

/**
 * One KPV deck: an AudioWorkletNode plus its control/meter bridges. The
 * AudioContext, live input, and inter-deck wiring live in the shared
 * AudioSystem — this class never connects itself.
 *
 * Continuous control flows through a SharedArrayBuffer when cross-origin
 * isolation grants one; otherwise throttled postMessage (~120 Hz).
 */
export class EngineHost {
  private node_: AudioWorkletNode | null = null;

  readonly ctrl: Float32Array;
  readonly meters: Float32Array;
  private readonly sabAvailable: boolean;
  private fallbackTimer: number | null = null;
  private ctrlDirty = false;

  private listeners = new Set<(e: EngineEvent) => void>();

  constructor(private readonly system: AudioSystem) {
    this.sabAvailable = typeof SharedArrayBuffer !== 'undefined' && crossOriginIsolated;
    if (this.sabAvailable) {
      this.ctrl = new Float32Array(new SharedArrayBuffer(Ctrl.__Size * 4));
      this.meters = new Float32Array(new SharedArrayBuffer(Meter.__Size * 4));
    } else {
      this.ctrl = new Float32Array(Ctrl.__Size);
      this.meters = new Float32Array(Meter.__Size);
    }
    this.ctrl[Ctrl.FxDepth] = 1;
    this.ctrl[Ctrl.Bpm] = 120;
    this.ctrl[Ctrl.InputGain] = 1;
    this.ctrl[Ctrl.OutputGain] = 1;
    this.ctrl[Ctrl.FxRelease] = 0.35;
    this.ctrl[Ctrl.Monitor] = 1;
  }

  get node(): AudioWorkletNode {
    if (!this.node_) throw new Error('deck not created');
    return this.node_;
  }

  get sampleRate(): number {
    return this.system.sampleRate;
  }

  on(fn: (e: EngineEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: EngineEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  /** Instantiate this deck's processor. The session wires it afterwards. */
  createNode(): void {
    if (this.node_) return;
    const node = new AudioWorkletNode(this.system.ctx, 'kpv-processor', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions: this.sabAvailable
        ? { ctrlSab: this.ctrl.buffer, meterSab: this.meters.buffer }
        : {},
    });
    node.port.onmessage = (e: MessageEvent<WorkletToHost>) => {
      const msg = e.data;
      switch (msg.type) {
        case 'ready':
          this.emit({ type: 'ready' });
          break;
        case 'meterFallback':
          this.meters.set(msg.values.subarray(0, this.meters.length));
          break;
        case 'sampleRecorded':
          this.emit({ type: 'sampleRecorded', bank: msg.bank, frames: msg.frames });
          break;
        case 'sampleOverview':
          this.emit({ type: 'sampleOverview', bank: msg.bank, peaks: msg.peaks });
          break;
        case 'log':
          console.warn('[KPV worklet]', msg.msg);
          this.emit({ type: 'log', msg: msg.msg });
          break;
      }
    };
    this.node_ = node;

    if (!this.sabAvailable) {
      this.fallbackTimer = window.setInterval(() => {
        if (!this.ctrlDirty) return;
        this.ctrlDirty = false;
        this.post({ type: 'ctrlFallback', values: this.ctrl.slice() });
      }, 8);
    }
  }

  dispose(): void {
    if (this.fallbackTimer !== null) window.clearInterval(this.fallbackTimer);
    this.node_?.disconnect();
    this.node_ = null;
  }

  // --- Continuous control ------------------------------------------------

  private set(idx: number, v: number): void {
    this.ctrl[idx] = v;
    this.ctrlDirty = true;
  }

  setTouch1(active: boolean, x: number, y: number): void {
    this.set(Ctrl.Touch1Active, active ? 1 : 0);
    if (active) {
      this.set(Ctrl.Touch1X, x);
      this.set(Ctrl.Touch1Y, y);
    }
  }

  setTouch2(active: boolean, x: number, y: number): void {
    this.set(Ctrl.Touch2Active, active ? 1 : 0);
    if (active) {
      this.set(Ctrl.Touch2X, x);
      this.set(Ctrl.Touch2Y, y);
    }
  }

  setFxDepth(v: number): void {
    this.set(Ctrl.FxDepth, v);
  }

  setHold(on: boolean): void {
    this.set(Ctrl.Hold, on ? 1 : 0);
  }

  setBpm(bpm: number): void {
    this.set(Ctrl.Bpm, bpm);
  }

  setInputGain(v: number): void {
    this.set(Ctrl.InputGain, v);
  }

  setOutputGain(v: number): void {
    this.set(Ctrl.OutputGain, v);
  }

  setMute(on: boolean): void {
    this.set(Ctrl.Mute, on ? 1 : 0);
  }

  setFxRelease(v: number): void {
    this.set(Ctrl.FxRelease, v);
  }

  setMonitor(v: number): void {
    this.set(Ctrl.Monitor, v);
  }

  // --- Discrete events ----------------------------------------------------

  private post(msg: HostToWorklet, transfer?: Transferable[]): void {
    this.node_?.port.postMessage(msg, transfer ?? []);
  }

  loadProgram(program: ProgramConfig): void {
    this.post({ type: 'loadProgram', program });
  }

  /** Align this deck's transport with the session master. */
  setBeat(beat: number): void {
    this.post({ type: 'setBeat', beat });
  }

  sampleTrigger(bank: number, on: boolean): void {
    this.post({ type: 'sampleTrigger', bank, on });
  }

  sampleRecord(bank: number, on: boolean, beats?: number, source?: RecordSource, freeLength?: boolean): void {
    this.post({ type: 'sampleRecord', bank, on, beats, source, freeLength });
  }

  sampleSlices(bank: number, slices: number, mask: number, mode: SliceMode): void {
    this.post({ type: 'sampleSlices', bank, slices, mask, mode });
  }

  sampleClear(bank: number): void {
    this.post({ type: 'sampleClear', bank });
  }

  sampleLoopMode(bank: number, loop: boolean): void {
    this.post({ type: 'sampleLoopMode', bank, loop });
  }

  /** Load raw PCM into a bank (buffers are transferred, not copied). */
  loadSampleData(bank: number, l: Float32Array, r: Float32Array, beats: number, loop: boolean): void {
    this.post({ type: 'sampleLoad', bank, l, r, beats, loop }, [l.buffer, r.buffer]);
  }

  /** Decode an audio file and load it into a bank (loop by default). */
  async loadSampleFile(bank: number, file: File | ArrayBuffer, beats = 4, loop = true): Promise<void> {
    const buf = file instanceof File ? await file.arrayBuffer() : file;
    const audio = await this.system.ctx.decodeAudioData(buf);
    const l = audio.getChannelData(0).slice();
    const r = (audio.numberOfChannels > 1 ? audio.getChannelData(1) : audio.getChannelData(0)).slice();
    this.post({ type: 'sampleLoad', bank, l, r, beats, loop }, [l.buffer, r.buffer]);
  }
}
